// Minimal toast/notification utility for DB write events
// Usage: showDbNotification({ table: 'projects', action: 'create', payload: { ... } })

export type DbNotification = {
  table: string;
  action: 'create' | 'update' | 'upsert' | 'delete' | string;
  payload?: Record<string, unknown> | undefined;
};

let containerEl: HTMLDivElement | null = null;

function ensureContainer(): HTMLDivElement {
  if (containerEl && document.body.contains(containerEl)) return containerEl;
  containerEl = document.createElement('div');
  containerEl.id = 'db-toast-container';
  Object.assign(containerEl.style, {
    position: 'fixed',
    bottom: '16px',
    right: '16px',
    zIndex: '2147483647',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    pointerEvents: 'none',
  } as CSSStyleDeclaration);
  document.body.appendChild(containerEl);
  return containerEl;
}

// Only enable in development unless explicitly overridden
const ENABLED: boolean = !!((import.meta as any)?.env?.DEV || ((import.meta as any)?.env?.VITE_SHOW_DB_TOASTS === 'true'));

export function showDbNotification({ table, action, payload }: DbNotification): void {
  if (!ENABLED) return; // no-op in production unless VITE_SHOW_DB_TOASTS=true
  const container = ensureContainer();

  const toast = document.createElement('div');
  Object.assign(toast.style, {
    background: 'rgba(17,24,39,0.96)',
    color: '#e5e7eb',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '10px',
    padding: '10px 12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
    fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontSize: '12px',
    lineHeight: '1.35',
    maxWidth: '360px',
    pointerEvents: 'auto',
    opacity: '0',
    transform: 'translateY(6px)',
    transition: 'opacity 120ms ease, transform 160ms ease',
  } as CSSStyleDeclaration);

  const title = document.createElement('div');
  title.textContent = `DB ${action} → ${table}`;
  Object.assign(title.style, {
    fontWeight: '700',
    color: '#bfdbfe',
    marginBottom: '4px',
  } as CSSStyleDeclaration);
  toast.appendChild(title);

  if (payload && Object.keys(payload).length > 0) {
    const pre = document.createElement('pre');
    pre.textContent = JSON.stringify(payload, null, 2);
    Object.assign(pre.style, {
      margin: 0,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      background: 'rgba(255,255,255,0.04)',
      borderRadius: '6px',
      padding: '6px',
      color: '#d1d5db',
      maxHeight: '200px',
      overflow: 'auto',
    } as CSSStyleDeclaration);
    toast.appendChild(pre);
  }

  container.appendChild(toast);

  // Entry animation
  requestAnimationFrame(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateY(0)';
  });

  // Auto dismiss
  const ttl = 3500;
  const timeoutId = window.setTimeout(() => dismiss(), ttl);

  function dismiss() {
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(6px)';
    window.setTimeout(() => {
      if (toast.parentElement) toast.parentElement.removeChild(toast);
    }, 180);
  }

  // Click to dismiss early
  toast.addEventListener('click', () => {
    window.clearTimeout(timeoutId);
    dismiss();
  });
}


